<template>
  <v-card flat>
    <v-row no-gutters>
      <v-col cols="12">
        <v-btn color="primary" icon @click="handleBack">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <span class="text-h6 mr-2" style="vertical-align: middle">
          {{ $t('message.pages.videoEditModal.subtitles') }}
        </span>
      </v-col>
    </v-row>

    <v-row no-gutters class="py-2 px-2">
      <v-col cols="12" class="pb-1" v-if="hasEditVideoPerm">
        <span>{{ $t('message.pages.videoEditModal.subtitlesText') }}</span>
      </v-col>
      <v-col cols="12" class="pt-5 pb-0" v-if="hasEditVideoPerm">
        <v-row>
          <v-col cols="4" class="pb-0 pt-3 pr-0">
            <v-select
              v-model="newSubtitle.label"
              :items="unusedLangCodes"
              :label="$t('message.pages.videoEditModal.language')"
              item-text="label"
              :menu-props="{ top: false, offsetY: true }"
              outlined
              dense
              :no-data-text="$t('message.pages.videoEditModal.usedSubtitles')"
              :messages="addSubtitleErrorMsg"
              :error="errors.addSubtitle"
              :disabled="loadings.subtitle || !hasEditVideoPerm"
              @change="stepByStepSubtitle.stepUploadSubtitle = true"
            >
              <template v-slot:message="{ message }">
                <span
                  class="red--text caption align-text"
                  v-if="errors.addSubtitle"
                >
                  <v-icon color="red" size="18" class="pb-1 pr-1">
                    mdi-alert-circle
                  </v-icon>
                  {{ message }}
                </span>
              </template>
            </v-select>
          </v-col>

          <v-scroll-y-transition>
            <v-col
              cols="5"
              class="mt-0 py-0 pr-0"
              v-if="stepByStepSubtitle.stepUploadSubtitle"
            >
              <v-file-input
                v-model="newSubtitle.file"
                truncate-length="15"
                :accept="['.vtt', '.srt']"
                :error="errors.wrongSubtitleFile"
                :disabled="loadings.subtitle"
                :messages="addSubtitleFileErrorMsg"
                @change="
                  handleFileInput, (stepByStepSubtitle.stepButtonSend = true)
                "
              >
                <template v-slot:message="{ message }">
                  <span
                    class="red--text caption align-text"
                    v-if="errors.wrongSubtitleFile"
                  >
                    <v-icon color="red" size="18" class="pb-1 pr-1">
                      mdi-alert-circle
                    </v-icon>
                    {{ message }}
                  </span>
                </template>
              </v-file-input>
            </v-col>
          </v-scroll-y-transition>

          <v-col cols="3" class="pr-0">
            <v-card outlined style="border: none">
              <v-scroll-y-transition>
                <v-btn
                  v-if="stepByStepSubtitle.stepButtonSend"
                  color="primary"
                  :disabled="loadings.subtitle || !formValidationSubtitle"
                  :loading="loadings.subtitle"
                  @click="handleAddSubtitle"
                  style="width: 100%"
                >
                  <v-icon>mdi-upload</v-icon>
                  <span class="pr-2"> {{ $t('message.send') }} </span>
                </v-btn>
              </v-scroll-y-transition>
            </v-card>
          </v-col>
        </v-row>
      </v-col>

      <v-card
        v-if="subtitles.length"
        v-scroll.self="onScroll"
        class="overflow-y-auto"
        flat
        max-height="160px"
        width="100%"
      >
        <v-row no-gutters>
          <v-col cols="12" v-for="(subtitle, i) in subtitles" :key="i">
            <v-card class="mt-2" outlined height="45px">
              <v-row
                class="pt-1 pr-4"
                no-gutters
                justify="center"
                align="center"
              >
                <v-icon class="pl-1">mdi-closed-caption</v-icon>
                <span class="pl-1 mr-2">{{ subtitle.label }}</span>
                <v-card flat color="rgb(227 232 238)">
                  <span
                    v-if="checkIfSubtitleIsDefault(subtitle)"
                    class="font-weight-medium px-2"
                    style="font-size: 12px"
                    >{{ $t('message.pages.videoEditModal.default') }}</span
                  >
                </v-card>
                <v-spacer />
                <v-progress-circular
                  v-if="
                    loadings.onSubtitleDelete &&
                    subtitle.srclang === srclangBeingRemoved
                  "
                  class="mr-2"
                  indeterminate
                  color="primary"
                  size="22"
                  width="3"
                ></v-progress-circular>
                <v-menu bottom left offset-y>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon v-bind="attrs" v-on="on">
                      <v-icon>mdi-dots-horizontal</v-icon>
                    </v-btn>
                  </template>
                  <v-list dense class="pa-0">
                    <v-list-item @click="handleUpdateDefaultSubtitle(subtitle)">
                      <v-list-item-title
                        v-if="checkIfSubtitleIsDefault(subtitle)"
                        >{{
                          $t('message.pages.videoEditModal.removeFromDefault')
                        }}</v-list-item-title
                      >
                      <v-list-item-title v-else>{{
                        $t('message.pages.videoEditModal.makeDefault')
                      }}</v-list-item-title>
                    </v-list-item>
                    <v-list-item
                      @click="handleRemoveSubtitle(subtitle.srclang)"
                    >
                      <v-list-item-title class="error--text">{{
                        $t('message.pages.videoEditModal.delete')
                      }}</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </v-row>
            </v-card>
          </v-col>
        </v-row>
      </v-card>
      <v-card v-else flat width="100%">
        <v-row no-gutters align="center" justify="center">
          <span>{{ $t('message.pages.videoEditModal.noSubtitleFound') }}</span>
        </v-row>
      </v-card>

      <v-card v-if="errors.subtitleRemove" class="mt-1" flat width="100%">
        <span class="red--text caption align-text">
          <v-icon color="red" size="18" class="pb-1 pr-1">
            mdi-alert-circle
          </v-icon>
          {{ errors.unknownErrorsMsg.subtitleRemove }}. Code:

          {{ errors.unknownErrorsCode.subtitleRemove }}
        </span>
      </v-card>

      <template v-if="hasSubtitles">
        <v-container
          class="container pa-4 mt-6 rounded"
          style="border: 1px solid #e5e5e5"
        >
          <v-row no-gutters>
            <v-col cols="12">
              <h4 style="font-weight: 500">
                {{ $t('message.pages.videoEditModal.subtitlesColorsSettings') }}
              </h4>
            </v-col>
          </v-row>
          <v-row no-gutters class="mt-4 mb-5">
            <!-- color: foreground -->
            <v-col cols="6" class="pa-0">
              <v-row no-gutters align="center">
                <v-menu
                  offset-x
                  v-model="colors.menuFg"
                  top
                  nudge-bottom="105"
                  nudge-left="16"
                  :close-on-content-click="false"
                >
                  <template v-slot:activator="{ on }">
                    <v-btn
                      class="ml-2"
                      :color="colors.foreground"
                      v-on="on"
                      :style="fgBtnStyle"
                      icon
                      elevation="3"
                    />
                  </template>
                  <v-color-picker
                    elevation="3"
                    v-model="colors.foreground"
                    flat
                    mode="hexa"
                    hide-mode-switch
                    :maxlength="7"
                    @input="changePlayer"
                  />
                </v-menu>
                <span class="ml-2">{{
                  $t('message.pages.video.mutedIndicator.fgColor')
                }}</span>
              </v-row>
            </v-col>
            <!-- color: background -->
            <v-col cols="6" class="pa-0">
              <v-row no-gutters align="center">
                <v-menu
                  offset-x
                  v-model="colors.menuBg"
                  top
                  nudge-bottom="105"
                  nudge-left="16"
                  :close-on-content-click="false"
                >
                  <template v-slot:activator="{ on }">
                    <v-btn
                      class="ml-2"
                      :color="colors.background"
                      v-on="on"
                      :style="bgBtnStyle"
                      icon
                      elevation="3"
                    />
                  </template>
                  <v-color-picker
                    elevation="3"
                    v-model="colors.background"
                    flat
                    mode="hexa"
                    hide-mode-switch
                    :maxlength="7"
                    @input="changePlayer"
                  />
                </v-menu>
                <span class="ml-2">{{
                  $t('message.pages.video.mutedIndicator.bgColor')
                }}</span>
              </v-row>
            </v-col>
          </v-row>
          <v-row no-gutters>
            <v-col cols="12">
              <h4 style="font-weight: 500">
                {{ $t('message.pages.videoEditModal.fontSizeSettings') }}
              </h4>
            </v-col>
          </v-row>
          <v-row no-gutters class="mt-4 mb-5">
            <!-- font: size -->
            <v-col cols="6" class="pa-0">
              <v-select
                v-model="newSubtitle.fontSize"
                :items="fontSizes"
                :label="
                  $t('message.pages.videoEditModal.fontSizeSettingsLabel')
                "
                item-text="label"
                :menu-props="{ top: false, offsetY: true }"
                outlined
                dense
                :messages="addSubtitleErrorMsg"
                :error="errors.addSubtitle"
                :disabled="loadings.subtitle"
                @change="changePlayer"
              ></v-select>
            </v-col>
          </v-row>
          <v-row no-gutters v-if="hasEditVideoPerm">
            <v-col cols="12">
              <v-card-actions class="pa-0">
                <v-spacer />
                <v-btn
                  class="mr-2 px-4"
                  text
                  :disabled="loadingButtonSave || withoutChanges"
                  @click="handleUndoChanges"
                >
                  {{ $t('message.pages.video.discardChanges') }}
                </v-btn>
                <v-btn
                  color="primary"
                  class="px-6"
                  :disabled="loadingButtonSave"
                  :loading="loadingButtonSave"
                  @click="handleUpdate"
                >
                  {{ $t('message.pages.video.publish') }}
                </v-btn>
              </v-card-actions>
            </v-col>
          </v-row>
        </v-container>
      </template>
    </v-row>

    <v-row v-if="!isLoading" no-gutters justify="end">
      <v-col cols="6">
        <v-card
          height="25"
          class="d-flex flex-row justify-end align-center"
          flat
        >
          <span
            class="red--text caption align-text justify-end"
            v-if="errors.failedToUpdateVideo"
          >
            <v-icon color="red" size="16" class="pb-1">
              mdi-alert-circle
            </v-icon>
            {{ $t('message.errors.networkError') }} {{ networkErrorMsg }}
          </span>
        </v-card>
      </v-col>
    </v-row>
  </v-card>
</template>

<script>
import { mapActions, mapMutations, mapState } from 'vuex'
import _ from 'lodash'
import i18n from '@/translations/lang'
import videosApi from '@/services/videosApi'
import colorHelpers from '@/utils/colorHelpers.js'

export default {
  name: 'VideoPlayerSubtitles',
  components: {},
  props: {
    handleBack: {
      type: Function,
      required: true,
    },
    liveVideoId: {
      required: false,
    },
  },
  data: () => ({
    stepByStepSubtitle: {
      stepChoiceLanguage: true,
      stepUploadSubtitle: false,
      stepButtonSend: false,
    },
    subtitles: [],
    newSubtitle: {
      label: '',
      srclang: '',
      file: null,
      fontSize: 'normal',
    },
    langCodes: [
      {
        label: i18n.t('message.pages.videoEditModal.languages.portuguese'),
        srclang: 'pt-BR',
      },
      {
        label: i18n.t('message.pages.videoEditModal.languages.english'),
        srclang: 'en',
      },
      {
        label: i18n.t('message.pages.videoEditModal.languages.spanish'),
        srclang: 'es',
      },
      {
        label: i18n.t('message.pages.videoEditModal.languages.french'),
        srclang: 'fr',
      },
      {
        label: i18n.t('message.pages.videoEditModal.languages.italian'),
        srclang: 'it',
      },
      {
        label: i18n.t('message.pages.videoEditModal.languages.japanese'),
        srclang: 'ja',
      },
      {
        label: i18n.t('message.pages.videoEditModal.languages.russian'),
        srclang: 'es',
      },
      {
        label: i18n.t('message.pages.videoEditModal.languages.vietnamese'),
        srclang: 'eh',
      },
      {
        label: i18n.t('message.pages.videoEditModal.languages.chinese'),
        srclang: 'cn',
      },
      {
        label: i18n.t('message.pages.videoEditModal.languages.arabian'),
        srclang: 'ae',
      },
      {
        label: i18n.t('message.pages.videoEditModal.languages.korean'),
        srclang: 'ko',
      },
    ],
    srclangBeingRemoved: '',
    errors: {
      addSubtitle: false,
      wrongSubtitleFile: false,
      deleteSubtitle: false,
      subtitleRemove: false,
      unknownErrorsCode: '',
      unknownErrorsMsg: '',
    },
    loadings: {
      subtitle: false,
      onSubtitleDelete: false,
    },
    isLoading: false,
    defaultSubtitle: false,
    colors: {
      menuFg: false,
      menuBg: false,
      foreground: '',
      background: '',
    },
    initialConfig: {},
    newSubtitleInitialConfig: {},
    loadingButtonSave: false,
    profileId: null,
  }),
  async mounted() {
    this.videoId = this.liveVideoId ? this.liveVideoId : this.$route.params.id
    this.subtitles = this.$store.state.video.video.config?.subtitles
    this.profileId = this.$store.state.video.video.config?.profile
    this.newSubtitle.fontSize = this.getFontSizeObject(
      this.$store.state.video.profile?.general?.captionsFontSize
    )
    this.colors.foreground = this.profile.colors.captionsColor
    this.colors.background = this.profile.colors.captionsBackgroundColor

    if (!this.subtitles) {
      this.subtitles = []
    }

    this.defaultSubtitle = this.$store.state.video.video.config?.defaultSubtitle

    this.$nextTick(() => {
      this.setColors()
      this.newSubtitleInitialConfig = _.cloneDeep(this.newSubtitle)
    })
  },
  computed: {
    ...mapState({
      video: (state) => state.video.video,
      profile: (state) => state.video.profile,
      profile_id: (state) => state.video.profile_id,
    }),
    hasEditVideoPerm() {
      return (
        this.$store.state.user.userPermissions?.includes('put-video') ?? false
      )
    },
    formValidationSubtitle() {
      return !!this.newSubtitle.label && this.newSubtitle.file instanceof File
    },
    unusedLangCodes() {
      return this.langCodes.filter((lang) => {
        return !this.subtitles.find((sub) => sub.srclang === lang.srclang)
      })
    },
    fontSizes() {
      return [
        {
          label: i18n.t('message.pages.videoEditModal.fontSizes.small'),
          value: 'small',
        },
        {
          label: i18n.t('message.pages.videoEditModal.fontSizes.normal'),
          value: 'normal',
        },
        {
          label: i18n.t('message.pages.videoEditModal.fontSizes.large'),
          value: 'large',
        },
      ]
    },
    pixelFontSizes() {
      return {
        small: 14,
        normal: 20,
        large: 36,
      }
    },
    addSubtitleFileErrorMsg() {
      if (this.errors.wrongSubtitleFile) {
        return i18n.t('message.pages.videoEditModal.errors.wrongSubtitleFile')
      }
      return ''
    },
    addSubtitleErrorMsg() {
      if (this.errors.addSubtitle) {
        return i18n.t('message.pages.videoEditModal.errors.defaultSubtitleErr')
      }
      return ''
    },
    hasSubtitles() {
      return this.subtitles.length > 0
    },
    fgBtnStyle() {
      return {
        backgroundColor: this.colors.foreground,
        borderRadius: '4px',
        transition: 'border-radius 200ms ease-in-out',
      }
    },
    bgBtnStyle() {
      return {
        backgroundColor: this.colors.background,
        borderRadius: '4px',
        transition: 'border-radius 200ms ease-in-out',
      }
    },
    withoutChanges() {
      return (
        _.isEqual(this.colors, this.initialConfig) &&
        _.isEqual(this.newSubtitle, this.newSubtitleInitialConfig)
      )
    },
  },
  watch: {
    colors: {
      handler({ foreground, background }) {
        if (foreground) this.setCaptionsColor(foreground)
        if (background) this.setCaptionsBackgroundColor(background)
      },
      deep: true,
      immediate: true,
    },
  },
  methods: {
    ...mapMutations('video', [
      'UPDATE_VIDEO',
      'UPDATE_VIDEO_PROFILE',
      'UPDATE_STATUS_ERROR',
    ]),
    ...mapActions('dashboard', [
      'setCaptionsColor',
      'setCaptionsBackgroundColor',
    ]),
    ...mapActions('video', [
      'updateProfile',
      'createProfile',
      'setProfileToVideos',
    ]),
    ...mapMutations('user', ['UPDATE_GLOBAL_SNACKBAR']),
    checkIfSubtitleIsDefault(subtitle) {
      return this.defaultSubtitle === subtitle.srclang
    },
    getFontSizeObject(size) {
      size = Number(size)
      if (size < 20) {
        return this.fontSizes[0].value
      } else if (size === 20 || !size) {
        return this.fontSizes[1].value
      }
      return this.fontSizes[2].value
    },
    async handleUpdateDefaultSubtitle(subtitle) {
      let updateDefaultSubtitleData = {
        video_id: this.videoId,
        defaultSubtitle: subtitle.srclang,
      }
      if (this.video.config.defaultSubtitle === subtitle.srclang) {
        updateDefaultSubtitleData.defaultSubtitle = false
        this.video.config.defaultSubtitle = false
        this.defaultSubtitle = false
      } else {
        this.video.config.defaultSubtitle = subtitle.srclang
        this.defaultSubtitle = subtitle.srclang
      }
      this.UPDATE_VIDEO(this.video)
      try {
        await videosApi.updateDefaultSubtitle(updateDefaultSubtitleData)
      } catch (e) {
        console.error(e)
      }
      this.$root.$emit('updateEmbedPreviewChange')
    },
    async handleAddSubtitle() {
      this.clearErrors()
      this.loadings.subtitle = true
      const data = new FormData()
      const langCode = this.langCodes.filter((lang) => {
        return lang.label === this.newSubtitle.label
      })
      this.newSubtitle.srclang = langCode[0].srclang
      data.append('file', this.newSubtitle.file)
      data.append('label', this.newSubtitle.label)
      data.append('srclang', this.newSubtitle.srclang)
      try {
        await videosApi.addSubtitleToVideo(this.videoId, data)
        this.loadings.subtitle = false
        this.subtitles.push({
          label: this.newSubtitle.label,
          srclang: this.newSubtitle.srclang,
        })
        this.newSubtitle = {
          label: '',
          srclang: '',
          file: null,
          fontSize: this.getFontSizeObject(
            this.$store.state.video.profile?.general?.captionsFontSize
          ),
        }
        this.stepByStepSubtitle.stepUploadSubtitle = false
        this.stepByStepSubtitle.stepButtonSend = false
      } catch (e) {
        this.loadings.subtitle = false
      }
      this.loadings.subtitle = false
      this.changeState()
    },
    async handleRemoveSubtitle(srclang) {
      this.clearErrors()
      this.clearErrorMsg()
      this.srclangBeingRemoved = srclang
      this.loadings.onSubtitleDelete = true
      try {
        await videosApi.deleteSubtitleFromVideo(this.videoId, srclang)
        this.loadings.onSubtitleDelete = false
        this.subtitles = this.subtitles.filter(
          (subtitle) => subtitle.srclang != srclang
        )
      } catch (e) {
        if (e.response.data) {
          this.errors.unknownErrorsCode = e.response.data.errCode
          this.errors.unknownErrorsMsg = e.response.data.errMsg
        } else {
          this.errors.unknownErrorsMsg = i18n.t(
            'message.errors.unexpectedError'
          )
        }
        this.errors.subtitleRemove = true
        this.loadings.onSubtitleDelete = false
      }
      this.loadings.onSubtitleDelete = false
      this.changeState()
    },
    handleFileInput(file) {
      if (!(file instanceof File)) {
        return
      }
      this.clearErrors()
      let fileExtension =
        file?.name?.substring(
          file?.name?.lastIndexOf('.') + 1,
          file?.name?.length
        ) || file?.name
      if (fileExtension !== 'vtt' || fileExtension !== 'srt') {
        this.errors.wrongSubtitleFile = true
        this.newSubtitle.file = null
        return
      }
    },
    clearErrors() {
      Object.keys(this.errors).forEach((e) => (this.errors[e] = false))
    },
    clearErrorMsg() {
      Object.keys(this.errors.unknownErrorsMsg).forEach(
        (e) => (this.errors.unknownErrorsMsg[e] = '')
      )
    },
    onScroll() {
      this.scrollInvoked++
    },
    changeState() {
      this.video.config.subtitles = this.subtitles
      this.UPDATE_VIDEO(this.video)
    },
    downloadSubtitle() {
      let label = 'en'
      if (this.newSubtitle.srclang === 'en') {
        label = 'en'
      } else if (this.newSubtitle.srclang === 'es') {
        label = 'es'
      } else if (this.newSubtitle.srclang === 'pt-BR') {
        label = 'pt-br'
      }
      // eslint-disable-next-line
      const domainMatch = new RegExp('^https://player-(.+).tv.pandavideo.com')
      const LIBRARY_ID = domainMatch.exec(this.video.video_player)
      //const isStaging = !!window.location.host.match(/staging/)
      //let redirectURL = `https://subtitles-staging.tv.pandavideo.com.br/${LIBRARY_ID[1]}/${this.video.video_external_id}/en.vtt`
      //if (isStaging) {
      //redirectURL = `https://subtitles-staging.tv.pandavideo.com.br/${LIBRARY_ID[1]}/${this.video.video_external_id}/${this.newSubtitle.srclang}.vtt`
      //}
      return `https://subtitles-staging.tv.pandavideo.com.br/${LIBRARY_ID[1]}/${this.video.video_external_id}/${label}.vtt`
    },
    setColors() {
      const { captionsColor, captionsBackgroundColor } = this.profile.colors

      this.colors.foreground = captionsColor
      this.colors.background = captionsBackgroundColor

      this.initialConfig = _.cloneDeep(this.colors)

      this.changePlayer()
    },
    changePlayer() {
      const hexToRgba = (value) => colorHelpers.hexToRgba(value)

      const iframe = document.getElementById('panda-player').contentWindow
      const foreground = hexToRgba(this.colors.foreground)
      const background = hexToRgba(this.colors.background)
      const fontSize = this.pixelFontSizes[this.newSubtitle.fontSize]

      this.profile.colors.captionsColor = this.colors.foreground
      this.profile.colors.captionsBackgroundColor = this.colors.background
      this.profile.general.captionsFontSize = fontSize
      this.UPDATE_STATUS_ERROR(false)
      this.UPDATE_VIDEO_PROFILE(this.profile)

      iframe.postMessage(
        {
          type: 'colors',
          parameter: {
            colors: {
              captionsColor: foreground,
              captionsBackgroundColor: background,
            },
          },
        },
        '*'
      )
    },
    handleUndoChanges() {
      this.colors = _.cloneDeep(this.initialConfig)
      this.newSubtitle = _.cloneDeep(this.newSubtitleInitialConfig)
      this.changePlayer()
    },
    async handleUpdate() {
      this.loadingButtonSave = true

      try {
        if (!this.video.config.profile) {
          await this.createProfile(this.videoId)
          this.video.config.profile = this.profile_id
          this.profileId = this.profile_id
          await this.setProfileToVideos([this.profileId, this.videoId])
        }

        await this.updateProfile([
          this.profileId,
          {
            colors: {
              ...this.profile.colors,
              captionsColor: this.colors.foreground,
              captionsBackgroundColor: this.colors.background,
            },
            general: {
              ...this.profile.general,
              captionsFontSize: this.pixelFontSizes[this.newSubtitle.fontSize],
            },
          },
        ])

        if (this.internalError) return

        this.handleSnackbar(this.$t('message.pages.video.changesSaved'))
        this.initialConfig = _.cloneDeep(this.colors)
        this.newSubtitleInitialConfig = _.cloneDeep(this.newSubtitle)
      } catch (error) {
        console.log(error)
      } finally {
        this.loadingButtonSave = false
      }
      this.$root.$emit('updateEmbedPreviewChange')
    },
    handleSnackbar(item) {
      const text = item || i18n.t('message.changesSaved')
      const open = true

      this.UPDATE_GLOBAL_SNACKBAR({ open, text })
    },
  },
}
</script>

<style></style>
